/**
 * Utility functions for constructing URLs
 */

import { WEB_BUYFLOW_DOMAIN, DEEP_LINK_BASE } from "@modules/common/constants";

import { isInsideIOSWebKit, isInsideAndroidWebView } from "./nativeNavigation";

export enum AddressChangeOrigin {
  SETTINGS = "SETTINGS",
  PRESCRIPTION_PAGE = "PRESCRIPTION_PAGE",
  INITIAL_INTAKE = "INITIAL_INTAKE",
  RENEWAL_INTAKE = "RENEWAL_INTAKE",
}

export type NavContext = "push" | "modal";

export type AuthenticatedUrlTitleSource = "fromUrl" | "fromDocument" | "hidden";

export type AuthenticatedUrlDeeplinkOptions = {
  /** How the new in-app web screen is presented. Defaults to the app's own default. */
  navContext?: NavContext;
  /** Where the new screen's nav bar title comes from. */
  titleSource?: AuthenticatedUrlTitleSource;
  /** Literal title, used with `titleSource: "fromUrl"`. */
  title?: string;
};

/**
 * Wraps an http(s) URL in an `open=AUTHENTICATED_URL` native deeplink, which
 * opens the embedded `url` in a fresh, authenticated in-app web screen.
 */
export const buildAuthenticatedUrlDeeplink = (
  targetUrl: string,
  options: AuthenticatedUrlDeeplinkOptions = {}
): string => {
  const params = ["open=AUTHENTICATED_URL"];

  if (options.navContext) {
    params.push(`navContext=${options.navContext}`);
  }
  if (options.titleSource) {
    params.push(`titleSource=${options.titleSource}`);
  }
  if (options.title !== undefined) {
    params.push(`title=${encodeURIComponent(options.title)}`);
  }
  params.push(`url=${encodeURIComponent(targetUrl)}`);

  return `${DEEP_LINK_BASE}?${params.join("&")}`;
};

/**
 * Constructs a buyflow URL for telehealth pages
 * @param page - The page route (e.g., "changeAddress", "landing")
 * @param accessCode - User's access code
 * @param userId - User's ID
 * @param origin - Optional origin parameter
 * @param navContext - Navigation context: "push" or "modal" (defaults to "push")
 * @param title - Optional title to display (will be URL encoded)
 * @param returnUrl - Optional return URL (will be URL encoded)
 * @returns The constructed buyflow URL or deeplink if on mobile
 */
export const getBuyflowUrl = (
  page: string,
  accessCode: string,
  userId: string,
  origin?: AddressChangeOrigin,
  navContext: NavContext = "push",
  title?: string,
  returnUrl?: string
): string => {
  const baseUrl = `${WEB_BUYFLOW_DOMAIN}/telehealth/`;
  
  // Check current page's query params
  const topLevelParams = new URLSearchParams(window.location.search);
  
  // Check if there's a "url" param - if so, use its query params instead
  let derivedParams = topLevelParams;
  const urlParam = topLevelParams.get("url");
  if (urlParam) {
    try {
      const decodedUrl = decodeURIComponent(urlParam);
      const url = new URL(decodedUrl, window.location.origin);
      derivedParams = url.searchParams;
    } catch {
      // If URL parsing fails, fall back to current params
      derivedParams = topLevelParams;
    }
  }
  
  const queryParams = new URLSearchParams({
    accessCode,
    userId,
  });
  
  // Check function param first, then query params for origin
  const originValue = origin || derivedParams.get("origin");
  if (originValue) {
    queryParams.set("origin", originValue);
  }
  
  if (returnUrl) {
    queryParams.set("returnUrl", encodeURIComponent(returnUrl));
  }

  const regularUrl = `${baseUrl}?${queryParams.toString()}#/${page}`;
  
  // turning this off for now because we need more mobile work to use it
  const isDeeplinkingEnabled = false
  // Check if we're on mobile (iOS or Android)
  const isIOS = isInsideIOSWebKit();
  const isAndroid = isInsideAndroidWebView();
  const isMobileApp = isIOS || isAndroid;
  
  if (isMobileApp && isDeeplinkingEnabled) {
    // Build top-level params for deep link
    const deepLinkParams = new URLSearchParams();
    deepLinkParams.set("open", "AUTHENTICATED_URL");
    deepLinkParams.set("navContext", navContext);

    if (title) {
      deepLinkParams.set("title", encodeURIComponent(title));
      deepLinkParams.set("titleSource", "fromUrl");
    }

    deepLinkParams.set("url", encodeURIComponent(regularUrl));
    
    return `${DEEP_LINK_BASE}?${deepLinkParams.toString()}`;
  }

  // For non-mobile, return regular URL
  return regularUrl;
};

